/**
 * AdminThemeProvider (ported from dink-admin DNKADM-0266) — the app-wide theme
 * mount: `ThemeModeProvider` (persisted light/dark/system preference, storage
 * key `tmm_reseller_admin.themeMode`) → a memoized `buildAdminTheme` of the resolved
 * mode → MUI `ThemeProvider` + `CssBaseline`. Replaces the former static
 * `<ThemeProvider theme={theme}>` in `main.tsx`.
 */
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeModeProvider } from '@/context/ThemeModeContext';
import { useThemeMode } from '@/context/theme-mode-context';
import { buildAdminTheme } from './theme';

interface AdminThemeProviderProps {
  children: ReactNode;
}

function ThemedApp({ children }: AdminThemeProviderProps) {
  const { resolvedMode } = useThemeMode();

  const theme = useMemo(() => buildAdminTheme(resolvedMode), [resolvedMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

export function AdminThemeProvider({ children }: AdminThemeProviderProps) {
  return (
    <ThemeModeProvider>
      <ThemedApp>{children}</ThemedApp>
    </ThemeModeProvider>
  );
}
